import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, EnvelopeSimple, PlugsConnected } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Card, btnGhost, btnPrimary, selectCls } from "../ui";
import { fmtDate } from "../assess/parts";
import { H } from "./parts";
import { ItAdminRequest } from "./ItAdminRequest";
import { OutlookConnect } from "./OutlookConnect";
import { AutoSend } from "./AutoSend";

function Status({ o }) {
  const c = o.connection || {};
  const [tone, title, text] = !o.configured
    ? ["amber", "Not set up yet", "Approval emails are kept ready in the app to copy and send by hand until Outlook is connected."]
    : c.reconnect_reason ? ["red", "Needs reconnecting", `Microsoft stopped accepting the saved sign-in (${c.reconnect_reason}). Press Connect Outlook and sign in again.`]
    : !c.connected ? ["amber", "Ready to connect", "Your IT admin's details are in. Press Connect Outlook and sign in to Microsoft as the warranty@ account."]
    : o.status?.error ? ["red", "Connection problem", o.status.error]
    : o.auto_send?.enabled ? ["green", `Connected as ${c.account?.username} · sending automatically`, `Approval and parts emails are sent from ${o.mailbox} as soon as a claim is approved.`]
    : ["green", `Connected as ${c.account?.username} · drafts only`, `Drafts go into the ${o.mailbox} Drafts folder. Nothing is sent automatically.`];
  const cls = { amber: "border-amber-500/40 bg-amber-500/[0.06] text-amber-200", green: "border-[#73bf44]/40 bg-[#73bf44]/[0.06] text-[#a6e07f]", red: "border-red-500/40 bg-red-500/[0.06] text-red-300" }[tone];
  return (
    <div className={`border rounded-xl px-4 py-3 ${cls}`} data-testid="wi-outlook-status">
      <div className="text-sm font-semibold">{title}</div><p className="text-xs opacity-80 mt-0.5 break-words">{text}</p>
      {o.status?.at && <p className="text-[0.65rem] opacity-60 mt-1">Last checked {fmtDate(o.status.at)}</p>}
    </div>
  );
}

function Counts({ c }) {
  const items = [["Waiting", c.waiting, "text-amber-400"], ["In Drafts", c.drafted, "text-[#73bf44]"], ["Sent", c.sent, "text-[#a6e07f]"], ["Sent by hand", c.handled, "text-neutral-300"], ["Failed", c.failed, "text-red-400"]];
  return (
    <div className="grid grid-cols-5 gap-2" data-testid="wi-outlook-counts">
      {items.map(([l, v, cls]) => <div key={l} className="bg-black/30 border border-neutral-800 rounded-lg px-2 py-2 text-center"><div className={`font-heading text-lg font-bold tabular-nums ${cls}`}>{v || 0}</div><div className="text-[0.6rem] text-neutral-500">{l}</div></div>)}
    </div>
  );
}

function PartsSettings({ s, onSaved }) {
  const [f, setF] = useState(s);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(s), [s]);
  const save = async () => {
    setBusy(true);
    try { onSaved((await adminApi.put("/admin/wi/email-settings", f)).data); toast.success("Parts request contacts saved"); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const field = (k, label, testId) => (
    <label className="block"><span className="text-[0.7rem] text-neutral-500">{label}</span>
      <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${selectCls} w-full mt-1`} data-testid={testId} /></label>
  );
  return (
    <div className="space-y-2.5" data-testid="wi-parts-settings">
      <H>Parts request goes to</H>
      <div className="grid grid-cols-2 gap-2">{field("factory_contact_name", "Name", "wi-parts-name")}{field("factory_recipient", "Email", "wi-parts-email")}</div>
      {field("parts_cc", "CC", "wi-parts-cc")}
      <button className={btnGhost} onClick={save} disabled={busy} data-testid="wi-parts-save-btn">Save contacts</button>
    </div>
  );
}

export function OutlookCard({ o, onChange }) {
  const [busy, setBusy] = useState(false);
  const run = async (path, msg) => {
    setBusy(true);
    try { const { data } = await adminApi.post(path, null, { timeout: 120000 }); onChange(data); toast.success(msg(data)); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  if (!o) return <Card title="Outlook emails" icon={EnvelopeSimple}><div className="h-40 animate-pulse" /></Card>;
  const c = o.connection || {};
  const live = o.configured && c.connected;
  return (
    <Card title="Outlook emails" icon={EnvelopeSimple} testId="wi-outlook-card">
      <div className="space-y-4">
        <p className="text-xs text-neutral-500">Mailbox: <span className="text-neutral-300">{o.mailbox}</span></p>
        <Status o={o} />
        <Counts c={o.counts} />
        {live && (
          <div className="flex flex-wrap gap-2">
            <button className={btnGhost} disabled={busy} onClick={() => run("/admin/wi/outlook/test", (d) => (d.status?.ok ? "Outlook connected" : "Outlook connection failed"))} data-testid="wi-outlook-test-btn"><PlugsConnected size={15} /> Test connection</button>
            {o.counts.waiting + o.counts.failed > 0 && <button className={btnPrimary} disabled={busy} onClick={() => run("/admin/wi/outlook/push", (d) => `${d.created} draft(s) created`)} data-testid="wi-outlook-push-btn"><ArrowsClockwise size={15} /> Create waiting drafts</button>}
            <button className={btnGhost} disabled={busy} onClick={() => run("/admin/wi/outlook/disconnect", () => "Outlook disconnected")} data-testid="wi-outlook-disconnect-btn"><PlugsConnected size={15} /> Disconnect</button>
          </div>
        )}
        {o.configured && <AutoSend o={o} onChange={onChange} />}
        {o.configured && !c.connected && <OutlookConnect o={o} onChange={onChange} />}
        {live && o.auto_send?.permission && !o.auto_send.permission.ok && <ItAdminRequest mailbox={o.mailbox} links={o.it_links} highlight />}
        {!o.configured && <ItAdminRequest mailbox={o.mailbox} links={null} />}
        <PartsSettings s={o.settings} onSaved={onChange} />
      </div>
    </Card>
  );
}
